import { useEffect, useId, useState } from "react";
import { Eye, EyeOff } from "lucide-react";
import { Modal, Button, FormField, Alert } from "../../ui";

const MIN_PASSWORD_LENGTH = 8;

export default function ChangePasswordModal({ isOpen, onClose, onSubmit }) {
  const [oldPassword, setOldPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPasswords, setShowPasswords] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const fieldId = useId();
  const formId = `${fieldId}-form`;

  useEffect(() => {
    setOldPassword("");
    setNewPassword("");
    setConfirmPassword("");
    setShowPasswords(false);
    setError("");
  }, [isOpen]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!oldPassword || !newPassword) {
      return setError("All fields are required.");
    }

    if (newPassword !== confirmPassword) {
      return setError("New passwords do not match.");
    }

    if (newPassword.length < MIN_PASSWORD_LENGTH) {
      return setError(`New password must be at least ${MIN_PASSWORD_LENGTH} characters.`);
    }

    setError("");
    setLoading(true);

    try {
      await onSubmit({ oldPassword, newPassword });
      onClose();
    } catch (err) {
      setError(err.response?.data?.message || err.message || "Something went wrong");
    }

    setLoading(false);
  };

  const inputType = showPasswords ? "text" : "password";
  const ToggleIcon = showPasswords ? EyeOff : Eye;

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Change Password" size="sm"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>Cancel</Button>
          <Button type="submit" form={formId} loading={loading}>Change Password</Button>
        </>
      }
    >
      {error && (
        <Alert variant="danger" className="mb-4" onDismiss={() => setError("")}>{error}</Alert>
      )}

      <form id={formId} onSubmit={handleSubmit} noValidate>
        <FormField label="Old Password" htmlFor={`${fieldId}-old`} required>
          <input
            id={`${fieldId}-old`}
            type={inputType}
            className="form-control"
            autoComplete="current-password"
            value={oldPassword}
            onChange={(e) => setOldPassword(e.target.value)}
            required
          />
        </FormField>

        <FormField
          label="New Password"
          htmlFor={`${fieldId}-new`}
          hint={`At least ${MIN_PASSWORD_LENGTH} characters.`}
          required
        >
          <input
            id={`${fieldId}-new`}
            type={inputType}
            className="form-control"
            autoComplete="new-password"
            minLength={MIN_PASSWORD_LENGTH}
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            required
          />
        </FormField>

        <FormField label="Confirm New Password" htmlFor={`${fieldId}-confirm`} required>
          <input
            id={`${fieldId}-confirm`}
            type={inputType}
            className="form-control"
            autoComplete="new-password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            required
          />
        </FormField>

        <Button
          variant="link"
          size="sm"
          className="!px-0"
          aria-pressed={showPasswords}
          onClick={() => setShowPasswords((shown) => !shown)}
        >
          <ToggleIcon className="w-4 h-4" aria-hidden="true" />
          {showPasswords ? "Hide passwords" : "Show passwords"}
        </Button>
      </form>
    </Modal>
  );
}
